import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle, XCircle, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from '../lib/toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('validating'); // validating, ready, success, error, no-token
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('no-token');
      return;
    }

    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/custom/validate-reset-token`, {
        token
      });
      
      setEmail(response.data.email || '');
      setStatus('ready');
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid or expired reset link';
      setErrorMessage(message);
      setStatus('error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/custom/reset-password`, {
        token,
        new_password: password
      });
      
      setStatus('success');
      toast.success('Password reset successfully!');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', { state: { passwordReset: true } });
      }, 3000);
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to reset password';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: 'var(--theme-bg)' }}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center mb-8">
          <img 
            src="/logo-wantd-navy.png"
            alt="WANTD" 
            className="h-12 w-auto"
          />
        </Link>

        {/* Card */}
        <div className="bg-[var(--theme-card)] border border-[var(--theme-border)] rounded-2xl p-8 shadow-card">
          
          {/* Validating State */}
          {status === 'validating' && (
            <div className="text-center py-8">
              <Loader2 className="w-12 h-12 mx-auto mb-4 text-[#C9956C] animate-spin" />
              <h1 className="text-xl font-bold text-[var(--theme-text-primary)] mb-2">
                Validating Reset Link
              </h1>
              <p className="text-[var(--theme-text-secondary)]">
                Please wait a moment...
              </p>
            </div>
          )}

          {/* Ready State - Show Form */}
          {status === 'ready' && (
            <>
              <h1 className="text-2xl font-bold text-[var(--theme-text-primary)] text-center mb-2">
                Create New Password
              </h1>
              <p className="text-[var(--theme-text-secondary)] text-center mb-8">
                Enter a new password for{' '}
                <span className="text-[var(--theme-text-primary)] font-medium">{email}</span>
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* New Password */}
                <div>
                  <label className="form-label">New Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--theme-text-muted)]" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter new password"
                      className="pl-10 pr-10 bg-[var(--theme-bg)] border-[var(--theme-border)] text-[var(--theme-text-primary)] placeholder:text-[var(--theme-text-muted)]"
                      required
                      minLength={6}
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--theme-text-muted)] hover:text-[var(--theme-text-secondary)]"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="form-label">Confirm Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--theme-text-muted)]" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      className="pl-10 bg-[var(--theme-bg)] border-[var(--theme-border)] text-[var(--theme-text-primary)] placeholder:text-[var(--theme-text-muted)]"
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                {/* Submit */}
                <Button 
                  type="submit" 
                  className="w-full bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white rounded-full h-12"
                  disabled={loading}
                  data-testid="reset-password-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Reset Password'
                  )}
                </Button>
              </form>
            </>
          )}

          {/* Success State */}
          {status === 'success' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-[var(--theme-text-primary)] mb-2">
                Password Reset!
              </h1>
              <p className="text-[var(--theme-text-secondary)] mb-6">
                Your password has been reset successfully.
              </p>
              <p className="text-sm text-[var(--theme-text-muted)] mb-6">
                Redirecting to login...
              </p>
              <Button 
                onClick={() => navigate('/login')}
                className="w-full bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white rounded-full h-12"
                data-testid="continue-to-login-btn"
              >
                Continue to Sign In
              </Button>
            </div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-[var(--theme-text-primary)] mb-2">
                Link Expired
              </h1>
              <p className="text-[var(--theme-text-secondary)] mb-6">
                {errorMessage}
              </p>
              <Button 
                onClick={() => navigate('/forgot-password')}
                className="w-full bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white rounded-full h-12"
                data-testid="request-new-link-btn"
              >
                Request New Reset Link
              </Button>
            </div>
          )}

          {/* No Token State */}
          {status === 'no-token' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
                <Lock className="w-8 h-8 text-amber-600" />
              </div>
              <h1 className="text-2xl font-bold text-[var(--theme-text-primary)] mb-2">
                Invalid Link
              </h1>
              <p className="text-[var(--theme-text-secondary)] mb-6">
                This password reset link is invalid or has expired.
              </p>
              <Button 
                onClick={() => navigate('/forgot-password')}
                className="w-full bg-[#1E3A5F] hover:bg-[#2D4A6F] text-white rounded-full h-12"
              >
                Request New Reset Link
              </Button>
            </div>
          )}

          {/* Back to Login */}
          <Link 
            to="/login" 
            className="flex items-center justify-center gap-2 mt-6 text-[var(--theme-text-secondary)] hover:text-[var(--theme-text-primary)]"
          >
            Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
}
